import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import courseService from '../services/courseService';
import { useAuth } from '../contexts/AuthContext';
import '../styles/CourseManagement.css';

const CourseManagement = () => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('title');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Check if user has app_editor role
  const isAppEditor = currentUser?.roles?.some(role => 
    role.name === 'app_editor' || role.name === 'admin' || role.name === 'superadmin'
  );
  
  useEffect(() => {
    fetchCourses();
  }, [appId]);
  
  const fetchCourses = async () => {
    try {
      setLoading(true);
      const data = await courseService.getCourses(appId);
      setCourses(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError('Failed to load courses. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteCourse = async (courseId) => {
    if (window.confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
      try {
        await courseService.deleteCourse(courseId);
        // Remove the deleted course from the state
        setCourses(courses.filter(course => course.id !== courseId));
      } catch (err) {
        console.error('Error deleting course:', err);
        setError('Failed to delete course. Please try again later.');
      }
    }
  };
  
  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field and default to ascending
      setSortBy(field);
      setSortOrder('asc');
    }
  };
  
  // Filter and sort courses
  const filteredAndSortedCourses = courses
    .filter(course => 
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  
  if (!isAppEditor) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          You do not have permission to access this page. Only app editors can manage courses.
        </div>
      </div>
    );
  }
  
  return (
    <div className="course-management container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Course Management</h1>
        <Link to={`/apps/${appId}/courses/new`} className="btn btn-primary">
          <i className="fas fa-plus"></i> Create New Course
        </Link>
      </div>
      
      {error && (
        <div className="alert alert-danger">{error}</div>
      )}
      
      <div className="card mb-4">
        <div className="card-body">
          <div className="input-group mb-3">
            <span className="input-group-text">
              <i className="fas fa-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          {loading ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center my-5">
              <p>No courses found. Create your first course to get started!</p>
              <Link to={`/apps/${appId}/courses/new`} className="btn btn-primary">
                <i className="fas fa-plus"></i> Create New Course
              </Link>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('title')} className="sortable">
                      Title
                      {sortBy === 'title' && (
                        <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th>Description</th>
                    <th onClick={() => handleSort('lesson_count')} className="sortable">
                      Lessons
                      {sortBy === 'lesson_count' && (
                        <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th onClick={() => handleSort('is_published')} className="sortable">
                      Status
                      {sortBy === 'is_published' && (
                        <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th onClick={() => handleSort('created_at')} className="sortable">
                      Created
                      {sortBy === 'created_at' && (
                        <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedCourses.map(course => (
                    <tr key={course.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          {course.thumbnail_url && (
                            <img 
                              src={course.thumbnail_url} 
                              alt={course.title} 
                              className="course-thumbnail me-2"
                            />
                          )}
                          <span>{course.title}</span>
                        </div>
                      </td>
                      <td>
                        <div className="course-description">
                          {course.description}
                        </div>
                      </td>
                      <td>{course.lesson_count || 0}</td>
                      <td>
                        <span className={`badge ${course.is_published ? 'bg-success' : 'bg-warning'}`}>
                          {course.is_published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td>{new Date(course.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className="btn-group">
                          <Link 
                            to={`/apps/${appId}/courses/${course.id}`} 
                            className="btn btn-sm btn-outline-primary"
                            title="View Course"
                          >
                            <i className="fas fa-eye"></i>
                          </Link>
                          <Link 
                            to={`/apps/${appId}/courses/${course.id}/edit`} 
                            className="btn btn-sm btn-outline-secondary"
                            title="Edit Course"
                          >
                            <i className="fas fa-edit"></i>
                          </Link>
                          <Link 
                            to={`/apps/${appId}/courses/${course.id}/lessons`} 
                            className="btn btn-sm btn-outline-info"
                            title="Manage Lessons"
                          >
                            <i className="fas fa-list"></i>
                          </Link>
                          <button 
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteCourse(course.id)}
                            title="Delete Course"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseManagement; 